"use client";

import {
  createContext,
  useContext,
  type ReactNode,
} from "react";
import { usePathname, useRouter } from "next/navigation";
import {
  type Lang,
  type Translations,
  useTranslations,
} from "@/lib/i18n";

const LangCtx = createContext<{ lang: Lang; t: Translations; toggle: () => void }>({
  lang: "en",
  t: {} as Translations,
  toggle: () => {},
});

export function useLang() {
  return useContext(LangCtx);
}

interface LangProviderProps {
  lang: Lang;
  children: ReactNode;
}

export function LangProvider({ lang, children }: LangProviderProps) {
  const t = useTranslations(lang);
  const pathname = usePathname();
  const router = useRouter();

  const toggle = () => {
    const COOKIE_OPTIONS = "path=/; max-age=31536000; SameSite=Lax";

    if (lang === "en") {
      // EN → FR: set cookie then navigate to FR mirror.
      document.cookie = `paul_lang=fr; ${COOKIE_OPTIONS}`;
      if (pathname === "/") {
        router.push("/fr");
      } else {
        router.push("/fr" + pathname);
      }
    } else {
      // FR → EN: set cookie then strip /fr prefix.
      document.cookie = `paul_lang=en; ${COOKIE_OPTIONS}`;
      if (pathname === "/fr") {
        router.push("/");
      } else {
        router.push(pathname.replace(/^\/fr/, "") || "/");
      }
    }
  };

  return (
    <LangCtx.Provider value={{ lang, t, toggle }}>{children}</LangCtx.Provider>
  );
}

// Keep default export for compatibility
export default LangProvider;
